import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
} from "@mui/material";
import { MAIN_URL } from "../../Configurations/Urls";
import Layout4 from "../DataLayouts/Layout4";
import TableDataGeneric from "../../Configurations/TableDataGeneric";
import ListSummaryCards, {
  countWhere,
} from "../../Components/List/ListSummaryCards";

const BASE_ROUTE = "/editions/access";

export const EDITION_COLUMNS = [
  {
    field: "edition_name",
    label: "Edition name",
    visible: true,
    width: 220,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "code",
    label: "Code",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_default_label",
    label: "Default",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_active_label",
    label: "Active",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ApplicationEditions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [editions, setEditions] = useState([]);
  const [appTab, setAppTab] = useState(0);

  const selectedApp = applications[appTab] || null;
  const applicationId = selectedApp?.application_id;

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${MAIN_URL}/api/core/application-editions`, {
        headers: authHeaders(),
      });
      const apps = res.data?.applications || [];
      setApplications(apps);
      const emsIndex = apps.findIndex((app) =>
        /ems/i.test(String(app.application_name || ""))
      );
      setAppTab(emsIndex >= 0 ? emsIndex : 0);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load applications."
      );
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEditions = useCallback(async (appId) => {
    if (!appId) {
      setEditions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${MAIN_URL}/api/core/application-editions`, {
        headers: authHeaders(),
        params: { application_id: appId },
      });
      setEditions(res.data?.editions || []);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load editions."
      );
      setEditions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    if (applicationId) {
      loadEditions(applicationId);
    }
  }, [applicationId, loadEditions]);

  const tableRows = useMemo(
    () =>
      (editions || []).map((edition) => ({
        ...edition,
        edition_name: edition.edition_name || edition.name || "—",
        is_default_label: edition.is_default ? "Default" : "—",
        is_active_label: edition.is_active !== false ? "Yes" : "No",
      })),
    [editions]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total editions",
        value: tableRows.length,
        iconKey: "layers",
      },
      {
        key: "default",
        label: "Default",
        value: countWhere(tableRows, (row) => Boolean(row.is_default)),
        iconKey: "check",
        tone: "ok",
      },
      {
        key: "active",
        label: "Active",
        value: countWhere(tableRows, (row) => row.is_active !== false),
        iconKey: "apps",
      },
    ],
    [tableRows]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`${BASE_ROUTE}/edit/${item.application_edition_id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(`${BASE_ROUTE}/view/${item.application_edition_id}`);
    },
    [navigate]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading="Application editions"
        subtitle="Select an application, then add or edit Proleed and Client editions."
        Route={BASE_ROUTE}
        btnName="Add Edition"
        addButtonDisabled={!applicationId}
        onAddBtClick={() =>
          navigate(`${BASE_ROUTE}/add?application_id=${applicationId}`)
        }
      />

      {loading && applications.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : applications.length === 0 ? (
        <Alert severity="info">No applications found.</Alert>
      ) : (
        <>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={appTab}
              onChange={(_, value) => setAppTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {applications.map((app) => (
                <Tab
                  key={app.application_id}
                  label={app.application_name || `App ${app.application_id}`}
                />
              ))}
            </Tabs>
          </Paper>

          <ListSummaryCards cards={summaryCards} />

          <TableDataGeneric
            tableName="application_editions"
            primaryKey="application_edition_id"
            heading="Application editions"
            data={tableRows}
            sortname="application_edition_id"
            showActions={true}
            Route={BASE_ROUTE}
            EditFunc={handleEdit}
            handleShow={handleShow}
            token={localStorage.getItem("token")}
            configss={EDITION_COLUMNS}
          />
        </>
      )}
    </>
  );
}
